<style>
    main {
        isolation: isolate;
    }

    .inventory-header {
        position: sticky;
        top: 0;
        z-index: 1;
        padding-top: 1.5rem;
        padding-bottom: 1.25rem;
        background: white;
    }

    input {
        min-height: 3rem;
        border-color: rgba(26, 32, 44, 0.2);
    }

    input:focus {
        outline: 2px solid #3182ce;
        outline-offset: -1px;
    }

    details + details {
        margin-top: 0.75rem;
    }

    summary {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 3rem;
        padding: 0.75rem 0;
        border-bottom: 1px solid rgba(26, 32, 44, 0.2);
        cursor: pointer;
        list-style: none;
    }

    summary::-webkit-details-marker {
        display: none;
    }

    summary:hover {
        background-color: rgba(26, 32, 44, 0.03);
    }

    summary:focus-visible {
        outline: 2px solid #3182ce;
        outline-offset: -2px;
    }

    .disclosure-indicator {
        flex-shrink: 0;
        margin-left: 1rem;
        font-size: 1.25rem;
        font-weight: 400;
    }

    li {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: baseline;
        gap: 1.5rem;
        padding: 0.875rem 0.5rem;
        border-bottom: 1px solid rgba(26, 32, 44, 0.08);
    }

    .negative-stock {
        background-color: #fff5f5;
        color: #9b2c2c;
        border-bottom-color: rgba(155, 44, 44, 0.16);
    }

    .product-name {
        min-width: 0;
        overflow-wrap: anywhere;
    }

    .quantity {
        text-align: right;
        font-variant-numeric: tabular-nums;
    }

    @media (min-width: 640px) {
        li {
            padding: 0.75rem 0.5rem;
        }
    }
</style>

<script>
    import { onMount } from 'svelte';
    import api from '../../api';
    import { buildSearch, clearOnEscape } from '../../utils';

    const presentaciones = ['Baby', 'Litro', 'Pistolas', 'Otros'];
    const formatoCantidad = new Intl.NumberFormat('es-MX');

    let productos = [];
    let busqueda = '';
    let cargando = true;
    let error = false;
    let categoriasAbiertas = {};

    // Reveal matches when a new search starts, including in collapsed groups.
    $: busqueda,
        (categoriasAbiertas = {
            Baby: true,
            Litro: true,
            Pistolas: true,
            Otros: true,
        });

    $: filtro = new RegExp(buildSearch(busqueda));
    $: grupos = presentaciones
        .map(presentacion => ({
            presentacion,
            productos: productos
                .filter(producto => producto.cantidad !== 0)
                .filter(
                    producto =>
                        (producto.presentacion || 'Otros') === presentacion
                )
                .filter(producto => filtro.test(producto.slug))
                .sort((a, b) =>
                    a.nombre.localeCompare(b.nombre, 'es', { numeric: true })
                ),
        }))
        .filter(grupo => grupo.productos.length);

    async function cargar() {
        cargando = true;
        error = false;
        try {
            const response = await api.get('productos/');
            productos = response.data;
        } catch (err) {
            error = true;
        } finally {
            cargando = false;
        }
    }

    onMount(cargar);
</script>

<svelte:head>
    <title>Inventario</title>
</svelte:head>

<main class="pb-8 text-gray-800">
    <div class="max-w-3xl px-4 mx-auto sm:px-6">
        <header class="inventory-header">
            <h1 class="mb-5 text-2xl font-semibold tracking-tight">
                Inventario
            </h1>
            <input
                type="search"
                name="buscar-inventario"
                aria-label="Buscar producto"
                placeholder="Buscar producto"
                autocomplete="off"
                class="w-full px-3 text-base"
                bind:value="{busqueda}"
                use:clearOnEscape
            />
        </header>

        {#if cargando}
            <p class="py-6 text-base text-gray-600" role="status">
                Cargando inventario…
            </p>
        {:else if error}
            <div class="py-6 text-base">
                <p role="alert">No se pudo cargar el inventario.</p>
                <button type="button" class="mt-4" on:click="{cargar}">
                    Reintentar
                </button>
            </div>
        {:else if !grupos.length}
            <p class="py-6 text-base text-gray-600" role="status">
                {busqueda ? 'No se encontraron productos con cantidad distinta de cero.' : 'No hay productos con cantidad distinta de cero.'}
            </p>
        {:else}
            {#each grupos as { presentacion, productos } (presentacion)}
                <details bind:open="{categoriasAbiertas[presentacion]}">
                    <summary>
                        <h2 class="text-lg font-semibold sm:text-base">
                            {presentacion}
                        </h2>
                        <span class="disclosure-indicator" aria-hidden="true">
                            {categoriasAbiertas[presentacion] ? '−' : '+'}
                        </span>
                    </summary>
                    <ul role="list" class="text-base sm:text-sm">
                        {#each productos as { slug, nombre, cantidad } (slug)}
                            <li class:negative-stock="{cantidad < 0}">
                                <span class="product-name">{nombre}</span>
                                <span class="quantity font-semibold">
                                    <span class="sr-only">Cantidad:</span>
                                    {formatoCantidad.format(cantidad)}
                                </span>
                            </li>
                        {/each}
                    </ul>
                </details>
            {/each}
        {/if}
    </div>
</main>
